import React from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

import { engagementApiRef } from 'plugin-engagement-tracker';

const IMPORT_TUTORIAL_LINK =
  'https://backstage.apptio.com/docs/default/component/backstage/software-catalog/basics/';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    clickable: {
      cursor: 'pointer',
    },
    tutorialLink: {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  }),
);

const RepoStatusCard = () => {
  const classes = useStyles();
  const engagementApi = useApi(engagementApiRef);

  let {
    value: repos,
    loading,
    error,
  } = useAsync(async () => await engagementApi.getReposStatusForUser());

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (!repos) {
    return (
      <ResponseErrorPanel error={new Error('Unknown error fetching repos')} />
    );
  }

  const openImportTutorial = () => {
    window.open(IMPORT_TUTORIAL_LINK, '_blank');
  };

  const openRepoTab = (repoUrl: string) => {
    window.open(repoUrl, '_blank');
  };

  const numRepos = repos.length;
  const numReposMissing = repos.filter(repo => !repo.isInCatalog).length;

  let progressPercent = 100;
  if (numRepos) {
    progressPercent = Math.floor(
      100 * ((numRepos - numReposMissing) / numRepos),
    );
  }
  const progressIsGood = progressPercent >= 60;

  let progressMessage;
  if (numRepos === 0) {
    progressMessage = 'You are not a member of any repos. That is unusual';
  } else if (numRepos === numReposMissing) {
    progressMessage = 'None of your repos are imported! Start today';
  } else if (numReposMissing === 0) {
    progressMessage = 'All of your repos are imported! Thank you';
  } else {
    progressMessage = `The catalog is missing ${numReposMissing} of your repos!`;
  }

  return (
    <InfoCard>
      <Typography variant="h6" component="h1">
        {progressMessage}
      </Typography>
      <Typography variant="body2" component="p" display="inline">
        Need help importing?&nbsp;
      </Typography>
      <Typography
        variant="body2"
        component="p"
        display="inline"
        className={classes.tutorialLink}
        onClick={openImportTutorial}
      >
        Check out this guide
      </Typography>
      <br />
      <br />
      <Progress
        value={progressPercent}
        color={progressIsGood ? 'primary' : 'secondary'}
        variant="determinate"
      />
      <List>
        {repos
          .sort((a, b) => {
            // List missing repos first, stable sort keeps them alphabetic
            return Number(a.isInCatalog) - Number(b.isInCatalog);
          })
          .map(repo => {
            return (
              <ListItem
                key={repo.repoPath}
                className={classes.clickable}
                onClick={() => openRepoTab(repo.githubUrl)}
              >
                <ListItemIcon>
                  {repo.isInCatalog ? (
                    <CheckCircleIcon color="primary" />
                  ) : (
                    <ErrorIcon color="secondary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={repo.repoName}
                  secondary={`${repo.githubUrl.slice('https://'.length)}`}
                />
              </ListItem>
            );
          })}
      </List>
    </InfoCard>
  );
};

export default RepoStatusCard;
